import { useEffect } from 'react';
import { useUnlockDialogContext } from './UnlockDialog.context';

type AutoUnlockGateProps = Readonly<{
  hasPages: boolean;
  hasUnlocked: boolean;
  isPreviewing: boolean;
  isWelcomeGateActive: boolean;
}>;

/**
 * Auto-opens the unlock dialog when a guest visits an event where all pages
 * are password-protected (or hidden) and the guest hasn't unlocked yet.
 *
 * Without this, the guest sees a blank page with no way to enter the password,
 * because the server filters out all passwordProtected pages when hasUnlocked is false.
 *
 * Re-opens the dialog if the guest dismisses it while still locked out,
 * since there's no content to show behind it.
 *
 * Suppressed when the full-page WelcomePagePasswordGate is active — that component
 * owns the unlock CTA in that case, so auto-opening the dialog on top would conflict.
 */
export const AutoUnlockGate: React.FC<AutoUnlockGateProps> = ({ hasPages, hasUnlocked, isPreviewing, isWelcomeGateActive }) => {
  const { isOpen, handleUnlockDialogOpen } = useUnlockDialogContext();

  const shouldAutoOpen = !hasPages && !hasUnlocked && !isPreviewing && !isWelcomeGateActive;

  useEffect(() => {
    if (shouldAutoOpen && !isOpen) {
      handleUnlockDialogOpen();
    }
  }, [shouldAutoOpen, isOpen, handleUnlockDialogOpen]);

  return null;
};
